import { LeagueConfig, Environment } from "@leagueplatform/core";
import { DEFAULT_LOCALE, getCurrentLocale } from "@leagueplatform/locales";
import { createReactRouterAdapter } from "@leagueplatform/react-router-adapter";
import { CustomToastMessage } from "components/forms/form-container/custom-toast-message.component";
import { LoadingSpinner } from "components/loading-spinner/loading-spinner.component";
import EN_LOCALES from "locales/en.json";
import ES_LOCALES from "locales/es-US.json";
import { questTheme } from "theme/theme";
import uploadDocumentIcon from "assets/upload-document.svg";
import careDocumentIcon from "assets/care-document.svg";
import deviceConnectionErrorIcon from "assets/device-connection-error.svg";
import removeProgramIcon from "assets/delete-account-warning.svg";
import documentsEmptyStateIcon from "assets/documents-empty-state.svg";

const {
  VITE_SEGMENT_KEY,
  VITE_WS_API_URL,
  VITE_API_URL,
  VITE_LEGACY_REST_API_URL,
  VITE_CONTENT_URL,
  VITE_AUTH0_DOMAIN,
  VITE_AUTH0_CLIENT_ID,
  VITE_APP_ENV,
  VITE_AUTH0_CONNECTION,
} = import.meta.env;

const CLIENT_ID = "quest-web";

const authConfig = {
  domain: VITE_AUTH0_DOMAIN!,
  client_id: VITE_AUTH0_CLIENT_ID!,
  redirect_uri: window.location.origin,
  connection: VITE_AUTH0_CONNECTION,
};

const analyticsConfig = {
  segmentKey: VITE_SEGMENT_KEY,
};

const apiConfig = {
  wsUrl: VITE_WS_API_URL!,
  url: VITE_API_URL!,
  legacyRestApi: VITE_LEGACY_REST_API_URL!,
};

export const config: LeagueConfig = {
  core: {
    clientId: CLIENT_ID,
    auth: { clientOptions: authConfig, idleSessionLifetimeHours: 0.25 }, // Default 15 minutes, should match the auth0 session inactivity timeout
    analytics: analyticsConfig,
    api: apiConfig,
    appEnvironment: VITE_APP_ENV as Environment,
    contentUrl: VITE_CONTENT_URL!,
    routing: {
      adapter: createReactRouterAdapter(),
    },
    ui: {
      theme: questTheme,
      components: {
        pageHeader: {
          shape: "line",
        },
        noPeekCarousel: {
          navButtonAppearance: "outlined",
        },
        removeProgramModal: {
          hideTitle: true,
        },
        programDetail: {
          showAchievementBlockTitle: true,
          showCategoryBadge: true,
        },
        loading: {
          element: <LoadingSpinner />,
          text: false,
        },
        toastMessage: { component: CustomToastMessage },
      },
    },
    i18n: {
      strings: {
        default: EN_LOCALES,
        en: EN_LOCALES,
        es: ES_LOCALES,
      },
    },
  },
  claims: {
    enableDynamicFilters: true,
    featureFlags: {
      shouldShowClaimDetails: true,
    },
  },
  careFinder: {
    schedulingEnabled: true,
  },
  careDiscovery: {
    masonryAppVersion: "2.0",
    masonryAppId: {
      left: "careLandingPage$root",
    },
  },
  wallet: {
    showLandingPageClaimsEntry: true,
  },
  careDocuments: {
    shouldUseDocumentsV2: true,
    emptyStateCopyForCategory: () => {
      const strings = getCurrentLocale(DEFAULT_LOCALE).startsWith("es")
        ? ES_LOCALES
        : EN_LOCALES;
      return {
        heading:
          strings.DOCUMENTS_EMPTY_STATE_HEADING ??
          EN_LOCALES.DOCUMENTS_EMPTY_STATE_HEADING,
        body:
          strings.DOCUMENTS_EMPTY_STATE_DESCRIPTION_QUEST ??
          EN_LOCALES.DOCUMENTS_EMPTY_STATE_DESCRIPTION_QUEST,
      };
    },
  },
  assets: {
    overrides: {
      FILE_UPLOAD_ICON: uploadDocumentIcon,
      CARE_DOCUMENT_ICON: careDocumentIcon,
      DEVICE_CONNECTION_ERROR_ICON: deviceConnectionErrorIcon,
      REMOVE_PROGRAM_IMAGE: removeProgramIcon,
      EMPTY_STATE_ICON: documentsEmptyStateIcon,
    },
  },
  dashboard: {
    labs: {
      labResultsLandingMasonryConfigKey: "labs$labResultsLanding$root",
      labResultDetailsMasonryConfigKey: "labs$labResultDetails$root",
    },
    metricDetailsMasonryConfigKey: "dashboard$metricDetails$root",
    allRecordedDataMasonryConfigKey: "dashboard$allRecordedData$root",
  },
  __internal: {
    shouldApplyCSSReset: true,
    shouldApplyLegacyGlobalStyles: true,
    shouldIgnoreDOMLangAttributeForLocale: true,
    shouldUseLegacyActivityToolboxLayout: true,
  },
};
